import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getUpgradeTargetIds,
  isValidUpgradeTarget,
} from "@/lib/productUpgradePaths";
import type { SkuDetail } from "@/hooks/queries/useSkuDetail";

const UPGRADE_SELECT = `
  id, name, description, long_description, voucher_terms, price,
  image_url, gallery_image_urls, slug, vendor_category, is_voucher,
  contents_list, partner_venues, included_inclusions, policy_text,
  availability_windows, parent_product_id, variant_label,
  vendor_partner_id, dietary_tags, lead_time_days, serves_min,
  serves_max,
  vendor_partners(vendor_name)
`;

function rowToSkuDetail(row: Record<string, unknown>): SkuDetail {
  const partner = row.vendor_partners as
    | { vendor_name?: string }
    | Array<{ vendor_name?: string }>
    | null;
  const partnerName = Array.isArray(partner)
    ? partner[0]?.vendor_name ?? "Lembra"
    : partner?.vendor_name ?? "Lembra";

  return {
    id: String(row.id),
    name: String(row.name ?? "Untitled"),
    description: (row.description as string | null) ?? null,
    long_description: (row.long_description as string | null) ?? null,
    voucher_terms: (row.voucher_terms as string | null) ?? null,
    price: Number(row.price ?? 0),
    image_url: (row.image_url as string | null) ?? null,
    gallery_image_urls: Array.isArray(row.gallery_image_urls)
      ? (row.gallery_image_urls as string[])
      : [],
    slug: (row.slug as string | null) ?? null,
    vendor_category: (row.vendor_category as string | null) ?? null,
    is_voucher: Boolean(row.is_voucher),
    contents_list: Array.isArray(row.contents_list)
      ? (row.contents_list as SkuDetail["contents_list"])
      : [],
    partner_venues: Array.isArray(row.partner_venues)
      ? (row.partner_venues as SkuDetail["partner_venues"])
      : [],
    included_inclusions: Array.isArray(row.included_inclusions)
      ? (row.included_inclusions as string[])
      : [],
    policy_text: (row.policy_text as string | null) ?? null,
    availability_windows: Array.isArray(row.availability_windows)
      ? (row.availability_windows as SkuDetail["availability_windows"])
      : [],
    parent_product_id: (row.parent_product_id as string | null) ?? null,
    variant_label: (row.variant_label as string | null) ?? null,
    vendor_name: partnerName,
    vendor_partner_id: String(row.vendor_partner_id),
    dietary_tags: Array.isArray(row.dietary_tags)
      ? (row.dietary_tags as string[])
      : [],
    lead_time_days: (row.lead_time_days as number | null) ?? null,
    serves_min: (row.serves_min as number | null) ?? null,
    serves_max: (row.serves_max as number | null) ?? null,
  };
}

async function fetchProductUpgrades(
  productId: string,
  basePrice: number,
  baseIsVoucher: boolean,
): Promise<SkuDetail[]> {
  const targetIds = [...getUpgradeTargetIds(productId)];
  if (targetIds.length === 0) return [];

  const { data, error } = await supabase
    .from("vendor_products")
    .select(UPGRADE_SELECT)
    .in("id", targetIds)
    .eq("is_available", true)
    .is("deleted_at", null);

  if (error) throw new Error(error.message);

  const byId = new Map(
    (data ?? [])
      .map((row) => rowToSkuDetail(row as Record<string, unknown>))
      .map((detail) => [detail.id, detail]),
  );

  return targetIds
    .map((id) => byId.get(id))
    .filter((detail): detail is SkuDetail => detail != null)
    .filter((detail) =>
      isValidUpgradeTarget(
        { price: basePrice, is_voucher: baseIsVoucher },
        { price: detail.price, is_voucher: detail.is_voucher },
      ),
    );
}

/** Type C upgrade SKUs linked to the active base product. */
export function useProductUpgrades(
  productId: string | null,
  basePrice: number,
  baseIsVoucher: boolean,
) {
  return useQuery({
    queryKey: ["product-upgrades", productId, basePrice, baseIsVoucher],
    queryFn: () => {
      if (!productId) return [];
      return fetchProductUpgrades(productId, basePrice, baseIsVoucher);
    },
    enabled: Boolean(productId),
    staleTime: 5 * 60 * 1000,
  });
}
